import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired" || attempts > 12) { setStatus("error"); return; }
      } catch { if (attempts > 12) { setStatus("error"); return; } }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
      <div className="mb-10"><Logo /></div>
      <div className="max-w-md rounded-xl border border-border/70 bg-card/50 p-10">
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-amber-500 mx-auto animate-spin" />
            <h1 className="font-display text-xl font-semibold mt-6">Confirming your subscription…</h1>
            <p className="text-sm text-muted-foreground mt-2">Hold tight while we verify payment with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h1 className="font-display text-2xl font-semibold mt-6">You're in.</h1>
            <p className="text-sm text-muted-foreground mt-2">Your DealsOps subscription is active. Time to build your first campaign.</p>
            <Button data-testid="go-dashboard-btn" onClick={() => navigate("/app")} className="mt-8 rounded-md bg-amber-500 text-black hover:bg-amber-400">Open the control plane</Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="h-12 w-12 text-rose-500 mx-auto" />
            <h1 className="font-display text-xl font-semibold mt-6">We couldn't confirm payment.</h1>
            <p className="text-sm text-muted-foreground mt-2">If you completed checkout, it may take a moment. You can continue to the app.</p>
            <Button onClick={() => navigate("/app")} variant="outline" className="mt-8 rounded-md">Continue anyway</Button>
          </>
        )}
      </div>
    </div>
  );
}
